import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Stack, Tooltip, Typography, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton, ListItemText,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { RowAction } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog, auditTrailFields } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import {
  useChannelHeads,
  useDeleteRelease,
  useDownloadArtefact,
  useMyPermissions,
  useReleaseCatalogue,
  useReleaseRollouts,
  useRolloutAction,
  useTransitionReleaseState,
  type ReleaseCatalogueEntry,
} from '../../api/queries';
import { useAlert, useConfirm, usePrompt } from '../../components/dialogs/DialogProvider';
import { NewReleaseDialog } from './NewReleaseDialog';
import { PromoteToHeadDialog } from './PromoteToHeadDialog';

/**
 * R5 — multi-target release catalogue. Sister page to the legacy
 * SoftwareReleasesPage (which covers the SC-container channel only). This
 * surface lists bundles for all targets (XU4 OS, Mac tool, ESP32 firmware,
 * MachineGateway, …) and exposes the R2 actions: promote state and
 * deprecate. Channel-head repoint / rollback are dialog-driven follow-ups.
 *
 * ACES-only audience.
 */
export default function ReleaseCataloguePage() {
  usePageTitle('Release catalogue');
  const [stateFilter, setStateFilter] = useState<string>('released');
  const [targetFilter, setTargetFilter] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  // Row-click read-only detail dialog (standard RM detail-form pattern).
  const [detailRow, setDetailRow] = useState<ReleaseCatalogueEntry | null>(null);
  const confirm = useConfirm();
  const prompt = usePrompt();

  const alert = useAlert();
  const perms = useMyPermissions();
  const canDelete = perms.data?.codes?.includes('provisioning.releases.delete') ?? false;

  const { queryKey, listFn } = useReleaseCatalogue(targetFilter, stateFilter);
  const transitionMut = useTransitionReleaseState();
  const deleteMut     = useDeleteRelease();
  const downloadMut   = useDownloadArtefact();

  // Artefact-download picker. A release with a single artefact downloads
  // straight from the ⋮ menu; one with several opens this dialog so the user
  // picks the role/file. `downloadingRole` drives a per-row spinner so a large
  // image (~600 MB streams through the API) shows it's in flight.
  const [downloadPick, setDownloadPick] = useState<ReleaseCatalogueEntry | null>(null);
  const [downloadingRole, setDownloadingRole] = useState<string | null>(null);

  // Streams one artefact to the local drive (see useDownloadArtefact). Errors
  // are surfaced — never silent (PORTAL_FORM_RULES: failures must be visible).
  const runDownload = async (
    row: ReleaseCatalogueEntry,
    artefact: ReleaseCatalogueEntry['artefacts'][number],
  ): Promise<void> => {
    setDownloadingRole(artefact.role);
    try {
      await downloadMut.mutateAsync({
        releaseId: row.id,
        role: artefact.role,
        fileName: artefact.originalFileName,
      });
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Download failed.';
      await alert({
        title: 'Download failed',
        severity: 'error',
        message: <>Could not download <strong>{artefact.role}</strong> for{' '}
          <strong>{row.target}@{row.version}</strong>. {msg}</>,
      });
    } finally {
      setDownloadingRole(null);
    }
  };

  // ⋮-menu entry point: 0 artefacts is filtered out by `visible`; 1 downloads
  // immediately; >1 opens the picker.
  const startDownload = async (row: ReleaseCatalogueEntry): Promise<void> => {
    if (row.artefacts.length === 1) {
      await runDownload(row, row.artefacts[0]);
    } else {
      setDownloadPick(row);
    }
  };

  // PR 2 — fetch current channel heads so we can render "stable head" /
  // "beta head" badges and let the action menu offer a Promote dialog
  // with the right default channel. Single query (target=null) returns
  // all heads at once; the page filters by row.target locally.
  const headsList = useChannelHeads(null);
  const headsQ = useQuery({
    queryKey: headsList.queryKey,
    queryFn:  headsList.listFn,
    staleTime: 15_000,
  });
  const headsByReleaseId = new Map<string, { channel: string }[]>();
  for (const h of headsQ.data ?? []) {
    if (!h.currentReleaseId) continue;
    const arr = headsByReleaseId.get(h.currentReleaseId) ?? [];
    arr.push({ channel: h.channel });
    headsByReleaseId.set(h.currentReleaseId, arr);
  }

  const [promoteTarget, setPromoteTarget] = useState<{
    row: ReleaseCatalogueEntry; defaultChannel: 'stable' | 'beta';
  } | null>(null);

  // PR 3 — in-flight rollouts banner. Refreshes every 15 s.
  const inFlightQ  = useReleaseRollouts('in_progress');
  const pausedQ    = useReleaseRollouts('paused');
  const rolloutMut = useRolloutAction();
  const activeRollouts = [...(inFlightQ.data ?? []), ...(pausedQ.data ?? [])];

  // Independent banner query — pulls drafts across all targets so the
  // "agent uploads awaiting review" count is visible no matter which
  // tab the user is currently looking at. TanStack dedupes when this
  // happens to align with the grid's own (target, state) tuple.
  const draftsAll = useReleaseCatalogue(null, 'draft');
  const draftAgentQ = useQuery({
    queryKey: draftsAll.queryKey,
    queryFn:  draftsAll.listFn,
    staleTime: 30_000,
  });
  const agentDrafts = (draftAgentQ.data ?? []).filter(r => r.createdBySystem);

  // Permanent delete flow — shared by the row ⋮ action and the detail-dialog
  // footer button so both paths use the same confirm prompt + success/error
  // surfacing. Returns true when the release was actually deleted (so a caller
  // can close the open detail dialog), false on cancel or failure.
  const runDeletePermanently = async (row: ReleaseCatalogueEntry): Promise<boolean> => {
    const reason = await prompt({
      title: `Delete release · ${row.target}@${row.version}`,
      message: <>
        <strong>Permanent.</strong> The release row and all <strong>{row.artefacts.length}</strong>
        {' '}artefact{row.artefacts.length === 1 ? '' : 's'} stored alongside it will be removed
        from the catalogue. Channel-head pointers that reference it must be repointed first
        or the server will refuse.
        <Box sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
          Devices already running this version are not affected — only the cloud catalogue
          entry and the bytea blobs are deleted.
        </Box>
      </>,
      placeholder: 'Reason (recorded in the audit log)',
      multiline: true,
      confirmLabel: 'Delete permanently',
      destructive: true,
    });
    if (reason === null) return false;  // cancelled
    try {
      const res = await deleteMut.mutateAsync({ id: row.id, reason: reason || undefined });
      await alert({
        title: 'Release deleted',
        severity: 'success',
        message: <>
          <strong>{res.target}@{res.version}</strong> removed.
          {' '}{res.artefactCount} artefact{res.artefactCount === 1 ? '' : 's'} cleared,
          {' '}{formatBytes(res.reclaimedBytes)} reclaimed.
        </>,
      });
      return true;
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Delete failed.';
      await alert({ title: 'Delete failed', severity: 'error', message: msg });
      return false;
    }
  };

  const cols: GridColDef<ReleaseCatalogueEntry>[] = [
    {
      field: 'target',
      headerName: 'Target',
      width: 110,
      renderCell: ({ value }) => <Chip label={value as string} size="small" color="primary" variant="outlined" />,
    },
    { field: 'version', headerName: 'Version', width: 120 },
    { field: 'displayName', headerName: 'Display name', width: 250 },
    {
      field: 'state',
      headerName: 'State',
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          label={value as string}
          size="small"
          color={value === 'released' ? 'success'
            : value === 'in_test' ? 'warning'
            : value === 'deprecated' ? 'default' : 'info'}
          variant={value === 'released' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'artefacts',
      headerName: 'Artefacts',
      width: 100,
      valueGetter: (_, row) => row.artefacts.length,
    },
    {
      field: 'createdBy',
      headerName: 'Submitted by',
      width: 170,
      sortable: false,
      renderCell: ({ row }) => row.createdBySystem ? (
        <Tooltip title="Submitted by an agent / CI service account. Channel forced to beta; requires human promotion before deployment.">
          <Chip
            size="small"
            color="warning"
            icon={<SmartToyIcon fontSize="small" />}
            label={row.createdByName ?? 'agent'}
          />
        </Tooltip>
      ) : (
        <Chip
          size="small"
          variant="outlined"
          icon={<PersonIcon fontSize="small" />}
          label={row.createdByName ?? '—'}
        />
      ),
    },
    {
      field: 'releasedAt',
      headerName: 'Released at',
      width: 170,
      renderCell: ({ value }) => fmtOrEmDash(value as string | null, fmtDateTime),
    },
    {
      // PR 2: current-head badges. If this release is the head for
      // stable AND/OR beta, render a coloured chip per channel so the
      // operator can see at a glance which release is live where.
      field: 'channelHeads',
      headerName: 'Live channels',
      width: 170,
      sortable: false,
      renderCell: ({ row }) => {
        const heads = headsByReleaseId.get(row.id) ?? [];
        if (heads.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
        return (
          <Stack direction="row" spacing={0.5}>
            {heads.map(h => (
              <Chip
                key={h.channel}
                size="small"
                label={`${h.channel} head`}
                color={h.channel === 'stable' ? 'success' : 'warning'}
                variant="filled"
              />
            ))}
          </Stack>
        );
      },
    },
  ];

  const exportColumns: ExportColumn<ReleaseCatalogueEntry>[] = [
    { header: 'Target', value: r => r.target },
    { header: 'Version', value: r => r.version },
    { header: 'Display name', value: r => r.displayName },
    { header: 'State', value: r => r.state },
    { header: 'Artefacts', value: r => String(r.artefacts.length) },
    { header: 'Released at', value: r => r.releasedAt ?? '—' },
  ];

  const actions: RowAction<ReleaseCatalogueEntry>[] = [
    {
      label: 'Download artefact…',
      icon: <FileDownloadIcon fontSize="small" />,
      visible: row => row.artefacts.length > 0,
      onClick: row => { void startDownload(row); },
    },
    {
      label: 'Promote (next state)',
      visible: row => ['draft', 'in_test'].includes(row.state),
      onClick: async row => {
        const next = row.state === 'draft' ? 'in_test' : 'released';
        const ok = await confirm({
          title: `Promote release · ${row.target}@${row.version}`,
          message: <>
            Move <strong>{row.displayName}</strong> from <code>{row.state}</code>
            {' '}→ <code>{next}</code>?
            {next === 'released' && (
              <Box sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
                Released artefacts become eligible for channel-head promotion.
                This does not deploy them — repointing the head is a separate action.
              </Box>
            )}
          </>,
          confirmLabel: 'Promote',
        });
        if (!ok) return;
        await transitionMut.mutateAsync({ id: row.id, state: next });
      },
    },
    {
      label: 'Promote to channel head…',
      // Visible when the release is eligible for at least one channel
      // (stable requires released; beta accepts released or in_test).
      visible: row => row.state === 'released' || row.state === 'in_test',
      onClick: row => {
        setPromoteTarget({
          row,
          defaultChannel: row.state === 'released' ? 'stable' : 'beta',
        });
      },
    },
    {
      // PR 2 — bidirectional demote. From released back to in_test so
      // an operator can pull a regression off stable without losing the
      // artefact. Server widens transitions accordingly.
      label: 'Demote to in_test',
      destructive: true,
      visible: row => row.state === 'released',
      onClick: async row => {
        const ok = await confirm({
          title: `Demote release · ${row.target}@${row.version}`,
          message: <>
            Move <strong>{row.displayName}</strong> from <code>released</code>
            {' '}→ <code>in_test</code>? Any channel head currently pointing at this
            release stays pointed at it — repoint or rollback the head separately
            if the demotion was triggered by a regression.
          </>,
          confirmLabel: 'Demote',
          destructive: true,
        });
        if (!ok) return;
        await transitionMut.mutateAsync({ id: row.id, state: 'in_test' });
      },
    },
    {
      // PR 2 — un-deprecate. Operator changed their mind or a hotfix
      // brings a deprecated artefact back into circulation.
      label: 'Restore from deprecated',
      visible: row => row.state === 'deprecated',
      onClick: async row => {
        const ok = await confirm({
          title: `Restore release · ${row.target}@${row.version}`,
          message: <>
            Move <strong>{row.displayName}</strong> from <code>deprecated</code>
            {' '}→ <code>released</code>? Releases that have been deleted from the
            catalogue can't be restored — this only works for entries that are
            still in the catalogue.
          </>,
          confirmLabel: 'Restore',
        });
        if (!ok) return;
        await transitionMut.mutateAsync({ id: row.id, state: 'released' });
      },
    },
    {
      label: 'Deprecate',
      destructive: true,
      visible: row => row.state !== 'deprecated',
      onClick: async row => {
        const reason = await prompt({
          title: `Deprecate release · ${row.target}@${row.version}`,
          message: <>
            Mark <strong>{row.displayName}</strong> as deprecated. Deprecated
            artefacts are not selectable for new channel heads. Existing devices
            already on this version are not affected.
          </>,
          placeholder: 'Reason (optional)',
          multiline: true,
          confirmLabel: 'Deprecate',
          destructive: true,
        });
        if (reason === null) return;  // user cancelled
        await transitionMut.mutateAsync({
          id: row.id,
          state: 'deprecated',
          deprecationReason: reason || undefined,
        });
      },
    },
    {
      label: 'Delete permanently',
      destructive: true,
      visible: row => canDelete && row.state === 'deprecated',
      onClick: row => { void runDeletePermanently(row); },
    },
  ];

  // Read-only detail view (PORTAL_FORM_RULES §5): full record + artefacts
  // table + audit-trail block, em-dash for nulls, local-TZ timestamps.
  const detailFields: DetailRow<ReleaseCatalogueEntry>[] = [
    { label: 'Target', render: r => <Chip label={r.target} size="small" color="primary" variant="outlined" /> },
    { label: 'Version', render: r => r.version },
    { label: 'Display name', render: r => r.displayName },
    {
      label: 'State',
      render: r => (
        <Chip
          label={r.state}
          size="small"
          color={r.state === 'released' ? 'success'
            : r.state === 'in_test' ? 'warning'
            : r.state === 'deprecated' ? 'default' : 'info'}
          variant={r.state === 'released' ? 'filled' : 'outlined'}
        />
      ),
    },
    { label: 'Channel', render: r => r.channel },
    {
      label: 'Live channels',
      render: r => {
        const heads = headsByReleaseId.get(r.id) ?? [];
        if (heads.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Stack direction="row" spacing={0.5}>
            {heads.map(h => (
              <Chip key={h.channel} size="small" label={`${h.channel} head`}
                    color={h.channel === 'stable' ? 'success' : 'warning'} variant="filled" />
            ))}
          </Stack>
        );
      },
    },
    {
      label: 'Submitted by',
      render: r => r.createdBySystem
        ? <Chip size="small" color="warning" icon={<SmartToyIcon fontSize="small" />} label={r.createdByName ?? 'agent'} />
        : <Chip size="small" variant="outlined" icon={<PersonIcon fontSize="small" />} label={r.createdByName ?? '—'} />,
    },
    { label: 'Released at', separator: true, render: r => fmtOrEmDash(r.releasedAt, fmtDateTime) },
    { label: 'Deprecated at', render: r => fmtOrEmDash(r.deprecatedAt, fmtDateTime) },
    {
      label: 'Artefacts',
      separator: true,
      fullWidth: true,
      render: r => r.artefacts.length === 0
        ? <Typography variant="body2" color="text.secondary">—</Typography>
        : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell>SHA-256</TableCell>
                  <TableCell align="center">Sig</TableCell>
                  <TableCell align="center">Manifest</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {r.artefacts.map(a => (
                  <TableRow key={a.role}>
                    <TableCell><Chip size="small" variant="outlined" label={a.role} /></TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{a.originalFileName ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatBytes(a.sizeBytes)}</TableCell>
                    <TableCell>
                      <Tooltip title={a.sha256}>
                        <Box component="code" sx={{ fontSize: 11 }}>{a.sha256.slice(0, 12)}…</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {a.hasSignature ? <CheckIcon fontSize="small" color="success" /> : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {a.hasManifest ? <CheckIcon fontSize="small" color="success" /> : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ),
    },
    {
      label: 'Release notes',
      separator: true,
      fullWidth: true,
      render: r => r.releaseNotesMd
        ? <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>{r.releaseNotesMd}</Box>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      label: 'Manifest',
      fullWidth: true,
      render: r => r.manifestJson
        ? <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 260, overflow: 'auto', fontSize: 11 }}>{prettyJson(r.manifestJson)}</Box>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    ...auditTrailFields<ReleaseCatalogueEntry>(),
  ];

  return (
    <>
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant="h5">Release catalogue</Typography>
        <Typography variant="body2" color="text.secondary">
          Single source of truth for every shipped artefact. <strong>Site Controller OS</strong>
          targets (C4, CM5) are full hardened images. <strong>SC runtime</strong> is the
          steady-state container that runs on top of a hardened image.
          <strong> ESP32-P4 firmware</strong> is the per-machine HAT.
          <strong> Mac tool</strong> is the technician laptop installer.
          ARMS-only publish + promote.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Note — channel-head changes (promoting a release, or flipping a site between
          stable and beta) can take up to <strong>~6 minutes</strong> to take effect on a
          given site controller: the SC caches its resolved channel for 5 minutes and
          then polls the head on its next 60-second runtime-fetcher tick.
        </Typography>

        {agentDrafts.length > 0 && (
          <Alert
            severity="warning"
            icon={<SmartToyIcon />}
            action={
              <Chip
                size="small"
                label="View drafts"
                onClick={() => setStateFilter('draft')}
                color="warning"
                variant="outlined"
                clickable
              />
            }
          >
            <strong>{agentDrafts.length}</strong> agent-uploaded
            {agentDrafts.length === 1 ? ' artefact is' : ' artefacts are'} awaiting human review.
            They are pinned to the <code>beta</code> channel and cannot deploy until an ACES
            user promotes them through <code>draft → in_test → released</code> and repoints the
            channel head.
          </Alert>
        )}

        {/* PR 3 — in-flight rollouts. Each row: target/channel, pacing,
            pause / resume / cancel. */}
        {activeRollouts.length > 0 && (
          <Alert severity="info" variant="outlined">
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {activeRollouts.length} staged rollout{activeRollouts.length === 1 ? '' : 's'} in flight
              </Typography>
              {activeRollouts.map(r => {
                // Estimated total duration = total_batches × interval.
                const totalBatches = Math.max(1, Math.ceil(Math.max(1, r.totalEligibleScs) / r.batchSize));
                const etaMin = Math.round((totalBatches * r.batchIntervalSeconds) / 60);
                return (
                  <Stack key={r.id} direction="row" spacing={1.5} alignItems="center" sx={{ fontSize: 13 }}>
                    <Chip size="small" color={r.status === 'paused' ? 'warning' : 'info'} label={r.status} />
                    <code>{r.target}/{r.channel}</code>
                    <Typography variant="caption" color="text.secondary">
                      {r.totalEligibleScs.toLocaleString()} SCs · batches of {r.batchSize}
                      {' '}every {Math.round(r.batchIntervalSeconds / 60)} min · ~{etaMin} min total
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {r.status === 'in_progress' && (
                      <Button size="small" onClick={() => rolloutMut.mutate({ id: r.id, action: 'pause' })}>
                        Pause
                      </Button>
                    )}
                    {r.status === 'paused' && (
                      <Button size="small" onClick={() => rolloutMut.mutate({ id: r.id, action: 'resume' })}>
                        Resume
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={async () => {
                        const reason = await prompt({
                          title: 'Cancel rollout',
                          message: <>Cancel the in-flight rollout for <code>{r.target}/{r.channel}</code>?
                            After cancellation, every SC converges on the head's current release at the next poll.</>,
                          placeholder: 'Reason (optional)',
                          confirmLabel: 'Cancel rollout',
                          destructive: true,
                        });
                        if (reason === null) return;
                        rolloutMut.mutate({ id: r.id, action: 'cancel', reason: reason || undefined });
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          </Alert>
        )}

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">State:</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={stateFilter}
            // Allow empty string (the "All" option) — `exclusive` returns
            // null when the user clicks the active button to deselect, so
            // explicitly check for null to keep the filter sticky.
            onChange={(_, v) => { if (v !== null) setStateFilter(v); }}
          >
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="released">Released</ToggleButton>
            <ToggleButton value="in_test">In test</ToggleButton>
            <ToggleButton value="draft">Draft</ToggleButton>
            <ToggleButton value="deprecated">Deprecated</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2">Target:</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={targetFilter}
            onChange={(_, v) => setTargetFilter(v)}
          >
            <ToggleButton value={null}>All</ToggleButton>
            <ToggleButton value="c4">C4</ToggleButton>
            <ToggleButton value="cm5-os">CM5-OS</ToggleButton>
            <ToggleButton value="cm4-os">CM4-OS</ToggleButton>
            <ToggleButton value="esp32p4">ESP32-P4</ToggleButton>
            <ToggleButton value="mac-tool">Mac tool</ToggleButton>
            <ToggleButton value="sc-runtime">SC runtime</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <RouteDataGrid<ReleaseCatalogueEntry>
          entityName="Release"
          queryKey={queryKey}
          listFn={listFn}
          columns={cols}
          exportColumns={exportColumns}
          actions={actions}
          onRowClick={(row) => setDetailRow(row)}
          reportTitle="Release catalogue"
          primaryAction={{
            label: '+ New release',
            onClick: () => setNewOpen(true),
          }}
        />

        <EntityDetailDialog<ReleaseCatalogueEntry>
          open={detailRow !== null}
          title={detailRow ? `${detailRow.displayName}` : 'Release'}
          data={detailRow}
          fields={detailFields}
          maxWidth="md"
          onClose={() => setDetailRow(null)}
          footerActions={
            detailRow && canDelete && detailRow.state === 'deprecated' ? (
              <Button
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={async () => {
                  const deleted = await runDeletePermanently(detailRow);
                  if (deleted) setDetailRow(null);
                }}
              >
                Delete permanently
              </Button>
            ) : undefined
          }
        />

        <NewReleaseDialog open={newOpen} onClose={() => setNewOpen(false)} />
        <PromoteToHeadDialog
          release={promoteTarget?.row ?? null}
          defaultChannel={promoteTarget?.defaultChannel ?? 'stable'}
          open={promoteTarget !== null}
          onClose={() => setPromoteTarget(null)}
        />

        {/* Multi-artefact download picker. Single-artefact releases skip this
            and download straight from the ⋮ menu. */}
        <Dialog
          open={downloadPick !== null}
          onClose={() => { if (!downloadingRole) setDownloadPick(null); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Download artefact{downloadPick ? ` · ${downloadPick.target}@${downloadPick.version}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This release has {downloadPick?.artefacts.length} artefacts. Choose one to save
              to your computer.
            </Typography>
            <List dense disablePadding>
              {downloadPick?.artefacts.map(a => {
                const busy = downloadingRole === a.role;
                return (
                  <ListItemButton
                    key={a.role}
                    disabled={downloadingRole !== null}
                    onClick={() => { if (downloadPick) void runDownload(downloadPick, a); }}
                  >
                    <Chip size="small" variant="outlined" label={a.role} sx={{ mr: 1.5 }} />
                    <ListItemText
                      primary={a.originalFileName ?? `${a.role}.bin`}
                      secondary={formatBytes(a.sizeBytes)}
                      primaryTypographyProps={{ sx: { wordBreak: 'break-all' } }}
                    />
                    <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                      {busy ? <CircularProgress size={18} /> : <FileDownloadIcon fontSize="small" color="action" />}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDownloadPick(null)} disabled={downloadingRole !== null}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Pretty-print a manifest JSON string for the detail dialog. Falls back to
// the raw string if it isn't valid JSON (never throws in render).
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
